import {
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import { Link } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { TypographyWithTooltip } from 'components/TypographyWithTooltip';
import { GeoEventMetadataText } from 'features/geoEvents/components/GeoEventMetadataRows';
import { GeoEventUpdateTime } from 'features/geoEvents/components/GeoEventUpdateTime';
import { useTranslation } from 'react-i18next';
import { useAuthState } from 'state/useAuthState';
import { getHighlightedSearchMatch } from './useMapSearch.utils';
import {
  GeoEventSearchResultDetails,
  SearchResult,
} from './useMapSearch.types';
import { EMPTY_DATA_VALUE } from '../../constants';

type GeoEventContainmentAcresRowProps = {
  containment?: number | null;
  acreage?: number | null;
  /** Search rows — fixed size; detail headers scale with body2. */
  dense?: boolean;
};

export const GeoEventContainmentAcresRow = (
  props: GeoEventContainmentAcresRowProps,
): JSX.Element | null => {
  const { containment, acreage, dense = false } = props;
  const { t } = useTranslation();

  if (containment === undefined && acreage === undefined) {
    return null;
  }

  return (
    <Stack
      direction="row"
      component="span"
      spacing={1}
      useFlexGap
      flexWrap="wrap"
      sx={dense ? { minWidth: 0, maxWidth: '100%' } : undefined}
    >
      {acreage !== undefined && (
        <GeoEventMetadataText dense={dense} truncate={false}>
          {t('geoEvent.acres')}:{' '}
          <Box component="span" fontWeight="fontWeightBold">
            {typeof acreage === 'number'
              ? acreage.toLocaleString()
              : EMPTY_DATA_VALUE}
          </Box>
        </GeoEventMetadataText>
      )}
      {containment !== undefined && (
        <GeoEventMetadataText
          dense={dense}
          truncate={false}
          sx={{ flexShrink: 0 }}
        >
          {t('geoEvent.containment')}:{' '}
          <Box component="span" fontWeight="fontWeightBold">
            {containment !== null ? `${containment}%` : EMPTY_DATA_VALUE}
          </Box>
        </GeoEventMetadataText>
      )}
    </Stack>
  );
};

// Non-incident sources; incident results come from `geo-event`, `inactive-geo-event`,
// and the default `wildfire` list.
const PLACE_SOURCE_IDS = ['google', 'mapbox', 'latlng'];

type SearchResultRowProps = {
  searchTerm?: string;
  searchResult: SearchResult;
  onBeforeClick?: () => void;
};

type SubtitleLayoutProps = {
  secondaryText?: string;
  geoEventDetails?: GeoEventSearchResultDetails;
};

const SubtitleLayout = (props: SubtitleLayoutProps): JSX.Element | null => {
  const { secondaryText, geoEventDetails } = props;

  if (!secondaryText && !geoEventDetails) {
    return null;
  }

  return (
    <Stack component="span" spacing={0.5}>
      {secondaryText && (
        <Typography
          variant="body2"
          color="text.secondary"
          component="span"
          sx={{ pb: '4px', display: 'block' }}
        >
          {secondaryText}
        </Typography>
      )}
      {geoEventDetails && (
        <>
          {'containment' in geoEventDetails && (
            <GeoEventContainmentAcresRow
              containment={geoEventDetails.containment}
              acreage={geoEventDetails.acreage}
              dense
            />
          )}
          <GeoEventUpdateTime geoEvent={geoEventDetails} dense />
        </>
      )}
    </Stack>
  );
};

export const SearchResultRow = (props: SearchResultRowProps): JSX.Element => {
  const { searchTerm, searchResult, onBeforeClick } = props;
  const {
    sourceId,
    action,
    avatar,
    primaryText,
    secondaryText,
    geoEventDetails,
    testId,
    urgencyIcon,
    hasInternalNotes,
  } = searchResult;

  const {
    permissions: { isInternalUser },
  } = useAuthState();
  const posthog = usePostHog();

  const handleClick = (): void => {
    if (PLACE_SOURCE_IDS.includes(sourceId)) {
      posthog.capture('clicked_non_incident_search_result', {
        sourceId,
      });
    }

    if (action === 'click') {
      onBeforeClick?.();
      searchResult.onClick();
    }
  };

  const hrefItemProps =
    action === 'link' ? { component: Link, to: searchResult.href } : {};

  return (
    <ListItemButton
      {...hrefItemProps}
      onClick={handleClick}
      dense
      divider
      sx={{
        alignItems: 'flex-start',
        paddingLeft: '2px', // default padding places centered icon too far right
      }}
      data-testid={testId}
    >
      <ListItemIcon
        sx={{
          mt: '4px',
          justifyContent: 'center',
        }}
      >
        {avatar}
      </ListItemIcon>
      <ListItemText
        primary={
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            component="span"
          >
            {isInternalUser && urgencyIcon}
            <TypographyWithTooltip
              component="span"
              title={primaryText}
              noWrap
              sx={{ letterSpacing: '-0.25px', fontWeight: 500, minWidth: 0 }}
            >
              {getHighlightedSearchMatch(searchTerm || '', primaryText)}
            </TypographyWithTooltip>
            {isInternalUser && hasInternalNotes && (
              <ArticleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            )}
          </Stack>
        }
        secondary={
          <SubtitleLayout
            secondaryText={secondaryText}
            geoEventDetails={geoEventDetails}
          />
        }
      />
    </ListItemButton>
  );
};
